import { CoreBox } from '@youscience/core';
import { OptionalReportRequest, OptionalReportSchema } from '@youscience/commonapp-service-common';

import SectionAccordion from '@components/SectionAccordion';

import { getFilename } from '../../../../common/formUtils';
import FileUpload from '../../../../common/FileUpload';

import { FormState } from '@constants/commonApp/types';

import { sxStyles } from './OtherUpdates.styles';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';
import { TabSections } from '@interfaces/recommenderWorkspace';

export type OtherUpdatesFields = NonNullable<Partial<OptionalReportRequest[TabSections.OtherUpdates]>>;

interface OtherUpdatesProps {
  otherUpdates?: OtherUpdatesFields | null;
  onFormChange: (formState: FormState<OtherUpdatesFields>) => void;
  onExpand: () => void;
  onContinue?: () => void;
  isExtended: boolean;
  isSaved: boolean;
  applicantName?: string;
  showError?: boolean;
}

const validator = OptionalReportSchema._def.schema.shape.otherUpdates;

export const OtherUpdates = ({
  otherUpdates = {},
  onFormChange,
  onExpand,
  isExtended,
  isSaved,
  applicantName = '',
  showError,
}: OtherUpdatesProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<OtherUpdatesFields, typeof validator>(
    otherUpdates as OtherUpdatesFields,
    validator,
    isSaved,
    onFormChange,
  );

  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';

  const handleFileChange = (field: string) => async (file: string | undefined) => {
    setFormStateWithErrors({ [field]: file });
  };

  return (
    <SectionAccordion
      title="Other updates"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !!formState.isFormValid}
      isError={showError || !formState.isFormValid}
    >
      <CoreBox sx={sxStyles.accordionDetails}>
        <FileUpload
          title="Please upload updates for this student's senior year courses, your evaluation, or any other corrections."
          name="changeExplanationUpload"
          initialFile={formState.data?.changeExplanationUpload}
          previewTitle="Other updates"
          filename={formState.data?.changeExplanationUpload ? getFilename(applicantName ?? '', 'updates') : undefined}
          onFileUpload={handleFileChange}
          onFileDelete={handleFileChange}
          error={formState.errors.changeExplanationUpload}
          maxFiles={3}
          initialFiles={formState.data?.changeExplanationOriginalFiles || []}
          onOriginalFilesChange={(files) => setFormStateWithErrors({ changeExplanationOriginalFiles: files })}
        />
      </CoreBox>
    </SectionAccordion>
  );
};
